import {
    Directive,
    OnInit,
    Input,
    ElementRef,
    inject,
    OnChanges,
    EventEmitter,
    HostBinding,
    HostListener,
    Output,
    AfterViewInit,
    OnDestroy,
    SimpleChanges
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe } from './pipes';
import { HelpersService } from '../shared/services/helpers.service';
import { createPopper, Instance } from '@popperjs/core';
import { DomSanitizer } from '@angular/platform-browser';

@Directive({
    selector: '[legacyLinkInner]',
    standalone: true
})
export class LegacyLinkInnerDirective implements OnInit {
    @Input() legacy: boolean;
    constructor(private el: ElementRef) {}

    fn = (e: PointerEvent) => {
        if (this.legacy) {
            e.preventDefault();
            e.stopPropagation();
            const parent = this.el.nativeElement.closest('a');
            if (parent?.href) {
                if (e.ctrlKey || e.metaKey) {
                    window.open(parent.href);
                } else window.location.href = parent.href;
            }
        }
    };

    ngOnInit() {
        this.el.nativeElement.addEventListener('click', this.fn);
    }

    ngOnDestroy() {
        this.el.nativeElement.removeEventListener('click', this.fn);
    }
}

/*
    no use cases atm.
    use case: [href] or href attributes that start with a string, are replaced with the api.medbook url on-compile (on staging and onward).
    internal angular routing + href: be sure to start with a variable.
    external api routing + href: be sure to NOT start with a variable.
    global match query: [href]="'/
*/
@Directive({
    selector: '[reloadOnRouterInnerLink]',
    standalone: true
})
export class ReloadOnRouterlinkInnerDirective implements OnInit {
    constructor(private el: ElementRef) {}

    fn = (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const parent = this.el.nativeElement.closest('a');
        if (parent?.href) {
            if (e.ctrlKey || e.metaKey) {
                window.open(parent.href);
            } else window.location.href = parent.href;
        }
    };

    ngOnInit() {
        this.el.nativeElement.addEventListener('click', this.fn);
    }

    ngOnDestroy() {
        this.el.nativeElement.removeEventListener('click', this.fn);
    }
}

@Directive({
    selector: '[disableOnDownload]',
    standalone: true
})
export class DisableOnDownloadDirective implements OnInit {
    @Input() timeout?: number;
    @Input() toast?: string;

    constructor(private el: ElementRef, private toastrService: ToastrService, private translatePipe: TranslatePipe) {}

    fn = () => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        (that.el.nativeElement as HTMLElement).classList.add('disabled');
        (that.el.nativeElement as HTMLElement).setAttribute('disabled', '');
        if (that.toast) setTimeout(() => that.toastrService.success(that.toast), 200);
        setTimeout(() => {
            (that.el.nativeElement as HTMLElement).classList.remove('disabled');
            (that.el.nativeElement as HTMLElement).removeAttribute('disabled');
        }, that.timeout ?? 800);
    };

    ngOnInit() {
        this.el.nativeElement.addEventListener('click', this.fn);
    }

    ngOnDestroy() {
        this.el.nativeElement.removeEventListener('click', this.fn);
    }
}

@Directive({
    selector: '[disableOnClick]',
    standalone: true
})
export class DisableOnClickDirective implements OnInit {
    @Input() timeout?: number;

    constructor(private el: ElementRef, private toastrService: ToastrService, private translatePipe: TranslatePipe) {}

    fn = () => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        (that.el.nativeElement as HTMLElement).classList.add('disabled');
        (that.el.nativeElement as HTMLElement).setAttribute('disabled', '');
        setTimeout(() => {
            (that.el.nativeElement as HTMLElement).classList.remove('disabled');
            (that.el.nativeElement as HTMLElement).removeAttribute('disabled');
        }, that.timeout ?? 800);
    };

    ngOnInit() {
        this.el.nativeElement.addEventListener('click', this.fn);
    }

    ngOnDestroy() {
        this.el.nativeElement.removeEventListener('click', this.fn);
    }
}

@Directive({
    selector: '[copyOnClick]',
    standalone: true
})
export class CopyOnClickDirective implements OnInit {
    @Input() copy: string;
    @Input() toast?: string;

    helpersService = inject(HelpersService);

    constructor(private el: ElementRef, private toastrService: ToastrService, private translatePipe: TranslatePipe) {}

    fn = (event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        that.helpersService.copyToClipboard(that.copy, that.toast);
    };

    ngOnInit() {
        this.el.nativeElement.addEventListener('click', this.fn);
    }

    ngOnDestroy() {
        this.el.nativeElement.removeEventListener('click', this.fn);
    }
}

@Directive({
    selector: '[ellipsisTooltip]',
    standalone: true
})
export class EllipsisTooltipDirective implements OnChanges {
    @Input() ellipsisTooltip: string | number = '';

    shown = false;
    tooltip: HTMLElement;
    popperInstance: Instance;

    sanitizer = inject(DomSanitizer);
    constructor(private el: ElementRef) {}

    show = () => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        if (!that.hasEllipsis()) return;
        that.tooltip.setAttribute('data-show', '');
        this.popperInstance.setOptions((options) => ({
            ...options,
            modifiers: [...options.modifiers, { name: 'eventListeners', enabled: true }]
        }));
        this.popperInstance.update();
        that.shown = true;
    };

    hide = () => {
        setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that = this;
            that.tooltip.removeAttribute('data-show');
            this.popperInstance.setOptions((options) => ({
                ...options,
                modifiers: [...options.modifiers, { name: 'eventListeners', enabled: false }]
            }));
            that.shown = false;
        }, 1);
    };

    ngOnChanges() {
        this.tooltip?.remove();
        this.init();
    }

    init() {
        setTimeout(() => {
            if (!this.ellipsisTooltip) return;
            if (!this.hasEllipsis()) return;
            const el = document.createElement('div');
            el.className = 'ellipsis-tooltip';
            el.innerHTML = this.sanitizer.sanitize(1, `${this.ellipsisTooltip}`);
            document.body.appendChild(el);
            this.tooltip = el;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that = this;

            this.popperInstance = createPopper(this.el.nativeElement, <HTMLElement>this.tooltip, {
                placement: 'top',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 0]
                        }
                    }
                ]
            });

            const showEvents = ['mouseenter', 'focus'];
            const hideEvents = ['mouseleave', 'blur'];

            showEvents.forEach((event) => {
                this.el.nativeElement.addEventListener(event, this.show);
            });

            hideEvents.forEach((event) => {
                this.el.nativeElement.addEventListener(event, this.hide);
            });
        }, 1);
    }

    hasEllipsis() {
        return this.el.nativeElement.offsetWidth < this.el.nativeElement.scrollWidth;
    }

    ngOnDestroy() {
        setTimeout(() => {
            const showEvents = ['mouseenter', 'focus'];
            const hideEvents = ['mouseleave', 'blur'];

            showEvents.forEach((event) => {
                this.el.nativeElement.addEventListener(event, this.show);
            });

            hideEvents.forEach((event) => {
                this.el.nativeElement.addEventListener(event, this.hide);
            });
            this.tooltip?.remove();
            this.popperInstance?.destroy();
        }, 1);
    }
}

@Directive({
    selector: '[fileDrop]',
    standalone: true
})
export class FileDropDirective {
    @HostBinding('class.file-over') fileOver: boolean;
    @Output() fileDropped = new EventEmitter<FileList>();

    // dragover listener
    @HostListener('dragover', ['$event']) onDragOver(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.fileOver = true;
    }

    // dragleave listener
    @HostListener('dragleave', ['$event']) public onDragLeave(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.fileOver = false;
    }

    // drop listener
    @HostListener('drop', ['$event']) public ondrop(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.fileOver = false;
        const files = evt.dataTransfer.files;
        if (files.length > 0) {
            this.fileDropped.emit(files);
        }
    }
}

@Directive({
    selector: '[replaceKeys]'
})
export class ReplaceKeysDirective implements OnChanges {
    @Input() replaceKeys: Record<string, string | null | undefined> = {};
    @Input() replaceContent = '';
    @Input() highlightContent: string | null = null; // optional

    helpersService = inject(HelpersService);

    constructor(private el: ElementRef<HTMLElement>) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['replaceKeys'] || changes['replaceContent'] || changes['highlightContent']) {
            this.updateContent();
        }
    }

    private updateContent() {
        if (!this.replaceContent) {
            this.el.nativeElement.innerHTML = '';
            return;
        }

        let content = this.replaceContent;

        for (const key in this.replaceKeys) {
            const value = this.replaceKeys[key];
            if (value != null) {
                const regex = new RegExp(this.escapeRegex(key), 'g');
                content = content.replace(regex, value);
            }
        }

        content = this.normalizeAnchorHrefs(content);

        if (this.highlightContent) {
            content = this.highlightText(content, this.highlightContent);
        }

        this.el.nativeElement.innerHTML = this.helpersService.sanitizeHtml(content);
    }

    private normalizeAnchorHrefs(html: string): string {
        const div = document.createElement('div');
        div.innerHTML = html;

        div.querySelectorAll('a[href]').forEach((anchor) => {
            const href = anchor.getAttribute('href')?.trim();
            if (!href) return;

            const hasSupportedProtocol = /^(https?:|mailto:|tel:)/i.test(href);
            const isInternalLink = href.startsWith('/') || href.startsWith('#');
            const isProtocolRelative = href.startsWith('//');

            if (!hasSupportedProtocol && !isInternalLink && !isProtocolRelative) {
                anchor.setAttribute('href', `https://${href.replace(/^\/+/, '')}`);
            }
        });

        return div.innerHTML;
    }

    private escapeRegex(str: string): string {
        // eslint-disable-next-line no-useless-escape
        return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    private highlightText(html: string, term: string): string {
        if (!term.trim()) return html;

        const normalize = (str: string) =>
            str
                .normalize('NFD')
                .replace(/\p{Diacritic}/gu, '')
                .toLowerCase();

        const searchNorm = normalize(term);
        const searchLen = searchNorm.length;

        const div = document.createElement('div');
        div.innerHTML = html;

        const textNodes: { node: Text; start: number; end: number }[] = [];
        let fullText = '';
        let pos = 0;

        const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
        let t: Text | null = walker.nextNode() as Text;

        while (t) {
            const text = t.textContent || '';
            const start = pos;
            const end = pos + text.length;
            textNodes.push({ node: t, start, end });
            fullText += text;
            pos = end;
            t = walker.nextNode() as Text;
        }

        const normalizedFull = normalize(fullText);

        const matches: { start: number; end: number }[] = [];
        for (let i = 0; i <= normalizedFull.length - searchLen; i++) {
            if (normalizedFull.slice(i, i + searchLen) === searchNorm) {
                matches.push({ start: i, end: i + searchLen });
            }
        }

        if (matches.length === 0) return html;

        for (const match of matches) {
            const remainingStart = match.start;
            const remainingEnd = match.end;

            for (const tn of textNodes) {
                if (tn.end <= remainingStart) continue;
                if (tn.start >= remainingEnd) break;

                const node = tn.node;
                const localStart = Math.max(0, remainingStart - tn.start);
                const localEnd = Math.min(node.textContent!.length, remainingEnd - tn.start);

                if (localStart < localEnd) {
                    const original = node.textContent!;

                    const before = original.slice(0, localStart);
                    const highlight = original.slice(localStart, localEnd);
                    const after = original.slice(localEnd);

                    const frag = document.createDocumentFragment();
                    if (before) frag.appendChild(document.createTextNode(before));

                    const span = document.createElement('span');
                    span.className = 'app-highlight';
                    span.textContent = highlight;
                    frag.appendChild(span);

                    if (after) frag.appendChild(document.createTextNode(after));

                    node.replaceWith(frag);

                    tn.node = null as any;
                }
            }

            const newTextNodes: { node: Text; start: number; end: number }[] = [];
            const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
            let curr = walker.nextNode();
            let cursor = 0;

            while (curr) {
                const text = (curr as Text).textContent || '';
                newTextNodes.push({
                    node: curr as Text,
                    start: cursor,
                    end: cursor + text.length
                });
                cursor += text.length;
                curr = walker.nextNode();
            }

            textNodes.splice(0, textNodes.length, ...newTextNodes);
        }

        return div.innerHTML;
    }
}

@Directive({
    selector: '[appHighlight]'
})
export class AppHighlightDirective implements OnChanges {
    @Input('appHighlight') term: string | null = null;
    @Input() content: string | null = null;

    helpersService = inject(HelpersService);

    constructor(private el: ElementRef<HTMLElement>) {}

    ngOnChanges(changes: SimpleChanges) {
        if ('term' in changes || 'content' in changes) {
            this.updateContent();
        }
    }

    private updateContent() {
        if (!this.content) {
            this.el.nativeElement.innerHTML = '';
            return;
        }

        const html = this.highlightText(this.content, this.term || '');
        this.el.nativeElement.innerHTML = this.helpersService.sanitizeHtml(html);
    }

    private escapeRegex(str: string): string {
        // eslint-disable-next-line no-useless-escape
        return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    private highlightText(html: string, term: string): string {
        if (!term) return html;

        const normalize = (str: string) =>
            str
                .toLowerCase()
                .normalize('NFD')
                .replace(/\p{Diacritic}/gu, '');

        const escapedTerm = this.escapeRegex(term);
        const regex = new RegExp(escapedTerm, 'gi');

        const div = document.createElement('div');
        div.innerHTML = this.helpersService.sanitizeHtml(html);

        const processNode = (node: ChildNode) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent || '';
                const replaced = text.replace(regex, (match) => {
                    if (normalize(match).includes(normalize(term))) {
                        return `<span class="app-highlight">${match}</span>`;
                    }
                    return match;
                });
                if (replaced !== text) {
                    const span = document.createElement('span');
                    span.innerHTML = this.helpersService.sanitizeHtml(replaced);
                    node.replaceWith(...Array.from(span.childNodes));
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                Array.from(node.childNodes).forEach(processNode);
            }
        };

        Array.from(div.childNodes).forEach(processNode);
        return div.innerHTML;
    }
}
